import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Observable, catchError, throwError, switchMap, shareReplay, finalize } from 'rxjs';
import { AuthService } from '../services/auth.service';

// Never try to refresh in response to the auth endpoints themselves — a 401 on
// login/register is "wrong password", and /refresh|/logout must not recurse.
const NON_REFRESHABLE = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

// Single-flight: the first 401 kicks off one refresh; every other request that
// 401s in the same window waits on that same in-flight refresh instead of
// firing its own (which would rotate the refresh token N times and trip reuse
// detection). Cleared when the refresh settles.
let refresh$: Observable<string> | null = null;

/**
 * On a 401, transparently rotate the access token via the refresh cookie and
 * retry the original request once. If the refresh fails, the original 401 is
 * propagated so the error interceptor (outer) performs the logout. Registered
 * INNER of the error interceptor so it sees the 401 first.
 */
export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  // Lazy — resolving AuthService eagerly here would re-enter its constructor on
  // the /me call the constructor itself fires (see error.interceptor.ts).
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: unknown) => {
      const is401 = error instanceof HttpErrorResponse && error.status === 401;
      const skip = NON_REFRESHABLE.some((path) => req.url.includes(path));
      if (!is401 || skip) return throwError(() => error);

      if (!refresh$) {
        refresh$ = injector.get(AuthService).refreshToken().pipe(
          shareReplay(1),
          finalize(() => {
            refresh$ = null;
          }),
        );
      }

      return refresh$.pipe(
        // Retry the original request with the freshly-minted access token.
        switchMap((newToken) => next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))),
        // Refresh (or the retry) failed → surface the ORIGINAL 401 so the error
        // interceptor logs out. Retrying only once avoids an infinite loop.
        catchError(() => throwError(() => error)),
      );
    }),
  );
};
